import { useState, useEffect } from 'react'
import styled from 'styled-components'
import clone from 'clone-deep'
import { AiOutlineDelete } from 'react-icons/ai'
import { toast } from 'react-toastify'
import { 
  useOrderStore,
  useConfigStore,
  useAccountStore,
  useProductStore
} from '../../state'
import UtsolgtImg from '../../graphics/utsolgt.png'

// Wizard steps
const STEPS = {
  PRODUCT_SELECTION: 0,
  QUANTITY_SELECTION: 1,
  GRINDING_OPTIONS: 2,
  REVIEW_ORDER: 3
}

export default function OrderWizard({ onCancel, onComplete, prefillOrder }) {
  const account = useAccountStore(s => s.account)
  const products = useProductStore(s => s.products)
  const transportFee = useConfigStore(s => s.transportFee)
  const postalCodesNoFee = useConfigStore(s => s.postalCodesNoFee)

  // Build prefill data from a previous order
  const buildPrefill = () => {
    if (!prefillOrder?.data?.products) return null
    const orderProducts = prefillOrder.data.products
    // Group by product id for quantity, but preserve per-bag grinds
    const grouped = {}
    const grinds = {}
    let bagIndex = {}
    for (const op of orderProducts) {
      const currentProduct = products.find(p => p.id === op.id)
      if (!currentProduct || currentProduct.data?.sold_out || currentProduct.data?.show === false) continue
      if (!grouped[op.id]) {
        grouped[op.id] = { ...clone(currentProduct), grind: op.grind || 'bønner', quantity: 1 }
        bagIndex[op.id] = 0
      } else {
        grouped[op.id].quantity += 1
      }
      // Store grind for each individual bag instance
      const instanceId = `${op.id}-${bagIndex[op.id]}`
      grinds[instanceId] = op.grind || 'bønner'
      bagIndex[op.id]++
    }
    const result = Object.values(grouped)
    return result.length > 0 ? { products: result, grinds } : null
  }

  const prefillData = prefillOrder ? buildPrefill() : null

  const [currentStep, setCurrentStep] = useState(prefillData ? STEPS.REVIEW_ORDER : STEPS.PRODUCT_SELECTION)
  const [selected_products, set_selected_products] = useState(prefillData?.products || [])
  const [prefillNotice] = useState(() => {
    if (!prefillOrder?.data?.products) return null
    const unavailable = prefillOrder.data.products.filter(op => {
      const current = products.find(p => p.id === op.id)
      return !current || current.data?.sold_out || current.data?.show === false
    })
    if (unavailable.length === 0) return null
    const names = [...new Set(unavailable.map(op => op.data?.name).filter(Boolean))]
    return `${names.join(', ')} er ikke lenger tilgjengelig og ble fjernet.`
  })
  
  // Check if transport fee should be added
  let add_transport_fee = true
  try {
    const postal_code = account?.data?.address?.postal_code
    if (postal_code) add_transport_fee = postalCodesNoFee.indexOf(parseInt(postal_code)) < 0
  } catch(e) {}

  // Calculate total price
  let selected_products_price = selected_products.reduce((sum, sp) => {
    const product = products.find(p => p.id === sp.id)
    const quantity = sp.quantity || 1
    return sum + ((product?.data?.price || 0) * quantity)
  }, 0)
  
  if (add_transport_fee) {
    selected_products_price += transportFee
  }

  const handleSelectProduct = (product_id) => {
    let prod = products.find(p => p.id === product_id) 
    if (prod?.data?.sold_out) return
    
    // Check if product is already selected
    const existingIndex = selected_products.findIndex(p => p.id === product_id)
    
    if (existingIndex >= 0) {
      // If already selected, remove it (toggle behavior)
      set_selected_products([
        ...selected_products.slice(0, existingIndex),
        ...selected_products.slice(existingIndex + 1)
      ])
    } else {
      // If not selected, add it with default values
      let p = clone(prod)
      p.grind = 'bønner'
      p.quantity = 1 // Default quantity
      set_selected_products(selected_products.concat(p))
    }
  }

  const handleDeleteSelectedProduct = (i) => {
    let sel_prods = [
      ...selected_products.slice(0, i), 
      ...selected_products.slice(i+ 1)
    ]
    set_selected_products(sel_prods)
  }

  const handleSetSelectedProductGrind = (i, grind) => {
    const sel_prods = selected_products.map((p,_i) => {
      if (_i === i) p.grind = grind
      return p
    })
    set_selected_products(sel_prods)
  }
  
  const handleSetSelectedProductQuantity = (i, quantity) => {
    // Ensure quantity is at least 1
    const numQuantity = Math.max(1, parseInt(quantity) || 1)
    
    const sel_prods = selected_products.map((p,_i) => {
      if (_i === i) p.quantity = numQuantity
      return p
    })
    set_selected_products(sel_prods)
  }
  
  // State to store individual grinding options for each expanded product
  const [expandedProductGrinds, setExpandedProductGrinds] = useState(prefillData?.grinds || {})
  
  // Function to expand products by quantity for grinding step
  const expandProductsByQuantity = () => {
    let expandedProducts = []
    
    selected_products.forEach(product => {
      const quantity = product.quantity || 1
      
      // Create multiple instances of the product based on quantity
      for (let i = 0; i < quantity; i++) {
        // Create a new object for each instance to avoid reference issues
        const productCopy = {...product}
        // Add a unique identifier to distinguish multiple instances of the same product
        const instanceId = `${product.id}-${i}`
        productCopy.instanceId = instanceId
        
        // Apply custom grinding option if set, otherwise use the default
        if (expandedProductGrinds[instanceId]) {
          productCopy.grind = expandedProductGrinds[instanceId]
        }
        
        // Reset quantity to 1 since we're expanding
        productCopy.quantity = 1
        expandedProducts.push(productCopy)
      }
    })
    
    return expandedProducts
  }
  
  // Function to update grinding option for a specific expanded product
  const updateExpandedProductGrind = (instanceId, grind) => {
    setExpandedProductGrinds(prev => ({
      ...prev,
      [instanceId]: grind
    }))
  }

  const handleCreateNewOrder = async () => {
    // We use the expanded products for submission so each bag can have its own grinding option
    const payload = {
      products: expandProductsByQuantity(),
      transport_fee: add_transport_fee ? transportFee : 0
    }
    
    // Remove the instanceId field from each product as it's just for internal use
    payload.products = payload.products.map(product => {
      const { instanceId, ...cleanProduct } = product
      return cleanProduct
    })
    
    const order_res = await fetch(`/api/orders`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    })
    if (order_res.status !== 200) {
      onCancel()
      return toast.error('Kunne ikke opprette bestillingen') 
    }
    const order_json = await order_res.json()
    window.location.href = order_json.redirectUrl
  }

  const handleNext = () => {
    if (currentStep < STEPS.REVIEW_ORDER) {
      setCurrentStep(currentStep + 1)
    } else {
      handleCreateNewOrder()
    }
  }

  const handleBack = () => {
    if (currentStep > STEPS.PRODUCT_SELECTION) {
      setCurrentStep(currentStep - 1)
    } else {
      onCancel()
    }
  }

  const productList = products
    .filter(p => p?.data?.bag)
    .filter(p => p?.data?.show !== false)
    .sort((a,b) => {
      if (a?.data?.order > b?.data.order) return 1
      if (a?.data?.order < b?.data.order) return -1
      return 0
    })
    .map(p => {
      const selected = selected_products.map(p => p.id).indexOf(p.id) >= 0
      return (
        <ProductItem key={p.id} selected={selected} onClick={() => handleSelectProduct(p.id)} soldout={`${p?.data?.sold_out}`}>
          <img className="productImage" src={p?.data?.bag} alt={p?.data?.name || 'Kaffepose'} />
          { p?.data?.sold_out &&
            <UtsolgtImage src={UtsolgtImg} alt="Utsolgt" />
          }
        </ProductItem>
      )
    })

  // Create the list of selected products with price
  const selectedProductsList = selected_products.map((sp, i) => {
    const product = products.find(p => p.id === sp.id)
    const quantity = sp.quantity || 1
    const totalItemPrice = product?.data?.price * quantity
    
    return (
      <SelectedProduct key={`${product.id}-${i}`}>
        <SelectedProductName>
          {product?.data?.name}
          {quantity > 1 && <span className="quantity">× {quantity}</span>}
        </SelectedProductName>
        <SelectedProductCost>{`${totalItemPrice},-`}</SelectedProductCost>
        <SelectedProductGrind>
          <select value={sp?.grind} onChange={(e) => handleSetSelectedProductGrind(i, e.target.value)}>
            <option value="bønner">Bønner</option>
            <option value="filtermalt">Filtermalt</option>
            <option value="kokmalt">Kokmalt</option>
            <option value="finmalt">Finmalt</option>
          </select>
        </SelectedProductGrind>
        <SelectedProductDelete onClick={() => handleDeleteSelectedProduct(i)}><AiOutlineDelete /></SelectedProductDelete>
      </SelectedProduct>
    )
  })

  // Add transport fee to the list if applicable
  const transportFeeItem = add_transport_fee ? (
    <SelectedProduct key="transport">
      <SelectedProductName>Frakt</SelectedProductName>
      <div style={{ width: '50px' }} />
      <SelectedProductCost>{`${transportFee},-`}</SelectedProductCost>
      <div style={{ flex: 'auto' }}></div>
    </SelectedProduct>
  ) : null

  // Render different content based on current step
  const renderStepContent = () => {
    switch (currentStep) {
      case STEPS.PRODUCT_SELECTION:
        return (
          <StepContent>
            <StepTitle>Steg 1: Velg produkt(er)</StepTitle>
            <StepDescription>
              Velg én eller flere produkter til din bestilling. Klikk på et produkt for å legge det til, klikk igjen for å fjerne det.
            </StepDescription>
            <ProductItemContainer>
              {productList}
            </ProductItemContainer>
            <StepActions>
              <button className="cancel" onClick={handleBack}>Avbryt</button>
              <button 
                className="purple" 
                onClick={handleNext}
                disabled={selected_products.length === 0}
              >
                Neste
              </button>
            </StepActions>
          </StepContent>
        )
      
      case STEPS.QUANTITY_SELECTION:
        return (
          <StepContent>
            <StepTitle>Steg 2: Velg antall</StepTitle>
            <StepDescription>
              Velg hvor mange av hver kaffetype du ønsker.
            </StepDescription>
            <ProductOptionsContainer>
              {selected_products.map((sp, i) => {
                const product = products.find(p => p.id === sp.id)
                return (
                  <QuantitySelectionItem key={`${product.id}-${i}`}>
                    <ProductImage>
                      <img src={product?.data?.bag} alt={product?.data?.name} />
                    </ProductImage>
                    <ProductInfo>
                      <div className="name">{product?.data?.name}</div>
                      <div className="price">{`NOK ${product?.data?.price},-`}</div>
                    </ProductInfo>
                    <QuantitySelector>
                      <button 
                        onClick={() => handleSetSelectedProductQuantity(i, (sp.quantity || 1) - 1)}
                        disabled={(sp.quantity || 1) <= 1}
                      >
                        −
                      </button>
                      <input 
                        type="number" 
                        min="1" 
                        value={sp.quantity || 1} 
                        onChange={(e) => handleSetSelectedProductQuantity(i, e.target.value)}
                      />
                      <button onClick={() => handleSetSelectedProductQuantity(i, (sp.quantity || 1) + 1)}>
                        +
                      </button>
                    </QuantitySelector>
                  </QuantitySelectionItem>
                )
              })}
            </ProductOptionsContainer>
            <StepActions>
              <button className="cancel" onClick={handleBack}>Tilbake</button>
              <button className="purple" onClick={handleNext}>Neste</button>
            </StepActions>
          </StepContent>
        )
      
      case STEPS.GRINDING_OPTIONS:
        return (
          <StepContent>
            <StepTitle>Steg 3: Velg malegrad</StepTitle>
            <StepDescription>
              Velg hvordan kaffen skal males for hver kaffepose.
            </StepDescription>
            <ProductOptionsContainer>
              {expandProductsByQuantity().map((sp, i) => {
                const product = products.find(p => p.id === sp.id)
                return (
                  <SelectedProduct key={sp.instanceId}>
                    <ProductImage>
                      <img src={product?.data?.bag} alt={product?.data?.name} />
                    </ProductImage>
                    <SelectedProductName>
                      {product?.data?.name}
                      {/* Add a small identifier to indicate which instance this is */}
                      <span className="bag-number">(Pose {i + 1})</span>
                    </SelectedProductName>
                    <div style={{ flex: 'auto' }} />
                    <SelectedProductGrind>
                      <select 
                        value={sp?.grind} 
                        onChange={(e) => {
                          // Update the specific grinding option for this instance
                          updateExpandedProductGrind(sp.instanceId, e.target.value)
                        }}
                      >
                        <option value="bønner">Bønner</option>
                        <option value="filtermalt">Filtermalt</option>
                        <option value="kokmalt">Kokmalt</option>
                        <option value="finmalt">Finmalt</option>
                      </select>
                    </SelectedProductGrind>
                  </SelectedProduct>
                )
              })}
            </ProductOptionsContainer>
            <StepActions>
              <button className="cancel" onClick={handleBack}>Tilbake</button>
              <button className="purple" onClick={handleNext}>Neste</button>
            </StepActions>
          </StepContent>
        )
      
      case STEPS.REVIEW_ORDER:
        return (
          <StepContent>
            <StepTitle>Steg 4: Bekreft bestilling</StepTitle>
            <StepDescription>
              Gjennomgå din bestilling før du sender den inn.
            </StepDescription>
            <OrderSummaryContainer>
              <h3>Oppsummering</h3>
              {expandProductsByQuantity().map((sp, i) => {
                const product = products.find(p => p.id === sp.id)
                return (
                  <SelectedProduct key={sp.instanceId}>
                    <ProductImage>
                      <img src={product?.data?.bag} alt={product?.data?.name} />
                    </ProductImage>
                    <SelectedProductName>
                      {product?.data?.name}
                      <span className="bag-number">(Pose {i + 1})</span>
                    </SelectedProductName>
                    <SelectedProductCost>{`${product?.data?.price},-`}</SelectedProductCost>
                    <SelectedProductGrind>
                      <span className="grind-label">{sp.grind}</span>
                    </SelectedProductGrind>
                  </SelectedProduct>
                )
              })}
              {transportFeeItem}
              <OrderTotal>
                <span>Totalt:</span>
                <span>{`NOK ${selected_products_price},-`}</span>
              </OrderTotal>
              
              {add_transport_fee && (
                <TransportFeeInfo>
                  Vi legger til NOK {transportFee},- for frakt på din bestilling.
                </TransportFeeInfo>
              )}
              {!add_transport_fee && (
                <TransportFeeInfo>
                  Du bor i et område med gratis levering!
                </TransportFeeInfo>
              )}
            </OrderSummaryContainer>
            <StepActions>
              <button className="cancel" onClick={handleBack}>Tilbake</button>
              <button className="purple" onClick={handleNext}>Bestill nå</button>
            </StepActions>
          </StepContent>
        )
      
      default:
        return null
    }
  }

  // Progress bar
  const progress = ((currentStep + 1) / (Object.keys(STEPS).length)) * 100

  return (
    <WizardContainer>
      <ProgressBarContainer>
        <ProgressBar progress={progress} />
      </ProgressBarContainer>
      <StepIndicator>
        <StepCircle active={currentStep === STEPS.PRODUCT_SELECTION} completed={currentStep > STEPS.PRODUCT_SELECTION}>1</StepCircle>
        <StepLine completed={currentStep > STEPS.PRODUCT_SELECTION} />
        <StepCircle active={currentStep === STEPS.QUANTITY_SELECTION} completed={currentStep > STEPS.QUANTITY_SELECTION}>2</StepCircle>
        <StepLine completed={currentStep > STEPS.QUANTITY_SELECTION} />
        <StepCircle active={currentStep === STEPS.GRINDING_OPTIONS} completed={currentStep > STEPS.GRINDING_OPTIONS}>3</StepCircle>
        <StepLine completed={currentStep > STEPS.GRINDING_OPTIONS} />
        <StepCircle active={currentStep === STEPS.REVIEW_ORDER} completed={currentStep > STEPS.REVIEW_ORDER}>4</StepCircle>
      </StepIndicator>
      {prefillNotice && <PrefillNotice>{prefillNotice}</PrefillNotice>}
      {renderStepContent()}
    </WizardContainer>
  )
}

const WizardContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-bottom: 20px;
`

const ProgressBarContainer = styled.div`
  width: 100%;
  height: 6px;
  background-color: #e0e0e0;
  border-radius: 3px;
  margin-bottom: 15px;
`

const ProgressBar = styled.div`
  height: 100%;
  width: ${props => props.progress}%;
  background-color: var(--color-vipps);
  border-radius: 3px;
  transition: width 0.3s ease;
`

const StepIndicator = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 20px;
`

const StepCircle = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background-color: ${props => props.active ? 'var(--color-vipps)' : props.completed ? 'var(--color-splash-purple)' : '#e0e0e0'};
  color: ${props => props.active || props.completed ? 'white' : 'black'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
`

const StepLine = styled.div`
  height: 2px;
  width: 50px;
  background-color: ${props => props.completed ? 'var(--color-splash-purple)' : '#e0e0e0'};
  margin: 0 10px;
  
  @media (max-width: 768px) {
    width: 30px;
  }
`

const PrefillNotice = styled.div`
  background-color: #fff3cd;
  color: #856404;
  padding: 10px 15px;
  border-radius: 5px;
  margin-bottom: 10px;
  font-size: 0.9em;
`

const StepContent = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px;
  border: 1px solid #e0e0e0;
  border-radius: 5px;
  background-color: white;
`

const StepTitle = styled.h2`
  margin-top: 0;
  margin-bottom: 10px;
  font-size: 1.5em;
`

const StepDescription = styled.p`
  margin-bottom: 20px;
  font-size: 1em;
  color: #666;
`

const StepActions = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
  
  button {
    min-width: 100px;
  }
  
  button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const ProductItemContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
  overflow-x: scroll;
  scrollbar-width: none;
  -ms-overflow-style: none;
  padding: 10px 0;
  
  &::-webkit-scrollbar {
    width: 0;
    height: 0;
  }
  
  @media (max-width: 768px) {
    font-size: 0.9em;
    gap: 5px;
  }
`

const ProductOptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const OrderSummaryContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  
  h3 {
    margin-top: 0;
    margin-bottom: 15px;
  }
`

const OrderTotal = styled.div`
  display: flex;
  justify-content: space-between;
  font-weight: bold;
  font-size: 1.2em;
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px dotted #ccc;
`

const TransportFeeInfo = styled.div`
  margin-top: 15px;
  padding: 10px;
  background-color: #f8f8f8;
  border-radius: 5px;
  font-style: italic;
`

const ProductItem = styled.div`
  display: flex;
  flex-direction: column;
  cursor: pointer;
  position: relative;
  
  & .productImage {
    width: 80px;
    @media (max-width: 768px) {
      width: 57px;
    }
    border: 2px solid ${props => props.selected ? 'var(--color-vipps)' : 'black'};
    ${props => props.soldout === 'true' ? 'filter: grayscale(100%);' : ''}
    border-radius: 5px;
  }
  
  &::after {
    content: ${props => props.selected ? '"✓"' : '""'};
    position: absolute;
    bottom: 8px;
    right: 8px;
    width: 18px;
    height: 18px;
    background-color: ${props => props.selected ? 'var(--color-vipps)' : 'transparent'};
    border-radius: 50%;
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: bold;
  }
`

const UtsolgtImage = styled.img`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 90%;
  transform: translate(-50%, -50%);
  border: 0px!important;
`

const SelectedProduct = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  padding: 8px;
  width: 100%;
  border-radius: 5px;
  background-color: #f9f9f9;
  
  &:hover {
    background-color: #f0f0f0;
  }
`

const SelectedProductName = styled.div`
  width: 120px;
  font-weight: bold;
  
  & .quantity {
    display: block;
    font-size: 0.9em;
    color: #666;
    font-weight: normal;
    margin-top: 2px;
  }
  
  & .bag-number {
    display: block;
    font-size: 0.8em;
    color: #666;
    font-weight: normal;
    margin-top: 2px;
    font-style: italic;
  }
`

const SelectedProductCost = styled.div`
  width: 70px;
  text-align: right;
  margin-right: 15px;
`

const SelectedProductGrind = styled.div`
  select {
    padding: 2px 5px;
    border-radius: 3px;
    font-size: 1em;
  }
  
  & .grind-label {
    text-transform: capitalize;
    background-color: #f0f0f0;
    padding: 2px 6px;
    border-radius: 3px;
    font-size: 0.9em;
  }
`

const SelectedProductDelete = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  color: #777;
  
  &:hover {
    color: var(--color-vipps);
  }
`

// Quantity selection step components
const QuantitySelectionItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 12px;
  background-color: #f9f9f9;
  border-radius: 8px;
  margin-bottom: 10px;
  
  &:hover {
    background-color: #f0f0f0;
  }
`

const ProductImage = styled.div`
  margin-right: 15px;
  
  & img {
    width: 40px;
    border-radius: 4px;
    border: 1px solid #ddd;
  }
`

const ProductInfo = styled.div`
  flex: 1;
  
  & .name {
    font-weight: bold;
    margin-bottom: 5px;
  }
  
  & .price {
    font-size: 0.9em;
    color: #666;
  }
`

const QuantitySelector = styled.div`
  display: flex;
  align-items: center;
  
  & button {
    width: 30px;
    height: 30px;
    background-color: #f0f0f0;
    border: 1px solid #ccc;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    cursor: pointer;
    
    &:hover:not(:disabled) {
      background-color: var(--color-splash-purple);
      color: white;
    }
    
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
  
  & input {
    width: 40px;
    height: 30px;
    text-align: center;
    margin: 0 5px;
    border: 1px solid #ccc;
    border-radius: 4px;
  }


`
